'use client'

import type { ReactNode } from 'react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { FilmBackdrop } from './FilmBackdrop'
import { CtaButtons } from './CtaButtons'
import { sectionInner, sectionShellAlt } from './SectionHeading'

// The page ends on one ask: the product name, the promise, and the button, over a film made
// with Martini, as the hero opens.
export function FinalCtaSection({
  title = 'Make something beautiful with Martini',
  line = 'Free to start. Join the thousands of professionals behind 200+ films made with Martini.',
  actions,
  fadeFrom = '#141414',
}: {
  title?: string
  line?: string
  /** Replaces the site-wide buttons, for pages with their own way in. */
  actions?: ReactNode
  /** The background of the section above, so the top fade starts from the same colour with no seam. */
  fadeFrom?: string
} = {}) {
  const sectionRef = useScrollAnimation<HTMLElement>()

  return (
    <section
      ref={sectionRef}
      id="get-started"
      className={`animate-on-scroll relative overflow-hidden ${sectionShellAlt} border-t-0`}
    >
      <FilmBackdrop />
      {/* Fade into the page above and the black footer below, with no rule at either edge. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-56"
        style={{ background: `linear-gradient(to bottom, ${fadeFrom}, transparent)` }}
      />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />

      <div className={`${sectionInner} relative flex flex-col items-center text-center`}>
        <h2 className="max-w-[1120px] text-[40px] font-semibold leading-[1.05] tracking-[-0.035em] md:text-[64px]">
          {title}
        </h2>
        <p className="mt-6 max-w-[760px] text-balance text-[17px] text-white/70 md:text-[19px]">{line}</p>
        {actions ?? <CtaButtons size="lg" className="mt-12 justify-center" />}
      </div>
    </section>
  )
}
